import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Select,
  Textarea,
  VStack,
  Grid,
  GridItem,
  HStack,
  Flex,
  FormErrorMessage,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Image,
  Text,
  Tooltip,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import DataTable from "./AppDataTable";
import {
  tableCustomStyles,
  ReservationStatusDot,
} from "../themes/TableStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faSyringe,
  faTable,
  faTruck,
  faSyringe as faSyringeIcon,
  faMoneyBillWave,
  faDollarSign,
  faTrash,
  faCalculator,
  faGears,
  faCapsules,
  faFileUpload,
  faEdit,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import CustomDatePicker, { getDateFromISOStringIgnoringTimezone } from "./CustomDatePicker";
import * as IconButton from "../themes/IconButtonSet";
import { useDropzone } from "react-dropzone";
import { StandardPageTitle, StandardHeader } from "../themes/StandardHeader";
import SearchableSelect from "./SearchableSelect";
import {
  TotalsSummary,
  VaccineDetailCards,
} from "./ReservationVaccineList";
import {
  buildComparableState,
  buildReservationDoc,
  fetchReservationForPrint,
  formatNumber,
  formatThaiDate,
  formatThaiDateTime,
  printReservationDoc,
} from "../utils/reservationDocument";

const StaffVaccineReservationDetail = () => {
  const navigate = useNavigate();
  const { reservationId } = useParams();
    const location = useLocation();
  const { sessionId, profile } = useAuth();
  const [formData, setFormData] = useState({
    ReservationID: reservationId || "RV-XXXX-XX-XX",
    ReservationDate: new Date(),
    FarmerID: profile?.FarmerID || "",
    FarmerName:
      [profile?.FirstName, profile?.LastName].filter(Boolean).join(' ') || "",
    RefTaxID: "",
    FarmerTel: profile?.Phone || "",
    FarmerEmail: profile?.Email || "",
    UsageObjective: "",
    DeliveryPeriod: "",
    PickupPoint: "",
    ReservedBy: profile?.Email || "",
    ReservationStatus: "ใบจองใหม่",
    ConfirmationEmailSent: false,
    ConsentGiven: true,
    Remark: "",
    Status: "Active",
    VaccineDetails: [],
    documentDate: new Date(),
    deliveryAddress: "",
    deliveryBy: "",
    trackingNumber: "",
    deliveryStaff: "",
  });

  const [periodEnd, setPeriodEnd] = useState(null); // Store PeriodEnd date
  const [pickupPoints, setPickupPoints] = useState([]);
  const [newVaccine, setNewVaccine] = useState({ id: "", unitreq: "" });
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [receiptFile, setReceiptFile] = useState(null);
  const [isEditingFile, setIsEditingFile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(!!reservationId);
  const [errors, setErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedNewStatus, setSelectedNewStatus] = useState("");
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  // สแนปช็อตข้อมูลที่บันทึกไว้ล่าสุด ใช้เทียบว่ามีการแก้ไขที่ยังไม่บันทึกหรือไม่
  const [savedState, setSavedState] = useState(null);
  const [isLeaveAlertOpen, setIsLeaveAlertOpen] = useState(false);
  const leaveCancelRef = useRef();

  // ตารางมี 6 คอลัมน์ จอที่แคบกว่า lg จึงเปลี่ยนไปแสดงเป็นการ์ดแทน
  const useCardView = useBreakpointValue({ base: true, lg: false });

  const pickupPointOptions = useMemo(
    () =>
      pickupPoints.map((point) => ({
        value: point.WarehouseID,
        label: point.WarehouseName,
      })),
    [pickupPoints]
  );

  const vaccineSelectOptions = useMemo(
    () =>
      vaccineOptions.map((vaccine) => ({
        value: vaccine.VaccineID,
        label: `${vaccine.VaccineID} - ${vaccine.VaccineNameTH}`,
      })),
    [vaccineOptions]
  );
  
    useEffect(() => {
      if (!sessionId) {
        navigate("/loginstaff", { state: { from: location.pathname } });
      }
    }, [sessionId, navigate, location.pathname]);

const fetchCycles = async () => {
  try {
    const response = await apiService({
      method: "POST",
      url: "/getVaccineOrderCycle",
      data: {},
      sessionId: sessionId || "",
    });

    if (response.result === "success") {
      if (!reservationId) {
        setFormData((prev) => ({
          ...prev,
          DeliveryPeriod: response.CycleName || "",
        }));
          // Fetch PeriodEnd by querying VaccineReservationPeriod
          const periodResponse = await apiService({
            method: "POST",
            url: "/getVaccineReservationPeriod", // Assume new endpoint to fetch period details
            data: { DeliveryPeriod: response.CycleName },
            sessionId: sessionId || "",
          });
          if (periodResponse.result === "success" && periodResponse.PeriodEnd) {
            setPeriodEnd(new Date(periodResponse.PeriodEnd));
          }
      }
    }
  } catch (error) {
    console.error("Error fetching cycles:", error);
  }
};

  const fetchPickupPoints = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinePickupPointList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setPickupPoints(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching pickup points:", error);
    }
  };

  const fetchWarehouseById = async (warehouseId) => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehousesByID",
        data: { WarehouseID: warehouseId },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        return response.WarehouseName || warehouseId; // Return WarehouseName or fallback to ID
      }
      return warehouseId; // Fallback to ID if API fails
    } catch (error) {
      console.error(`Error fetching warehouse ${warehouseId}:`, error);
      return warehouseId; // Fallback to ID on error
    }
  };

  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesReservationList",
        data: {},
        sessionId: sessionId || "",
      });
      console.log("Response from /getVaccinesList:", response);
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
        return response.VaccinesList || [];
      }
      return [];
    } catch (error) {
      console.error("Error fetching vaccines:", error);
      return [];
    }
  };

  const fetchReservation = async (id, vaccines) => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineReservationsByID",
        data: { ReservationID: id },
        sessionId: sessionId || "",
      });
      console.log("Response from /getVaccineReservationsByID:", response);
      if (response.result === "success") {
        const reservation = response.VaccineReservation;
        const details = response.VaccineReservationDetails || [];
        const updatedDetails = details.map((detail) => {
          const vaccine = vaccines.find((v) => v.VaccineID === detail.VaccineID);
          if (!vaccine) {
            console.warn(`VaccineID ${detail.VaccineID} not found in vaccineOptions`);
          }
          const packageSize = vaccine?.PackageSize || 1;
          const doseCount = Math.ceil(detail.RequestQuantity * packageSize);
          return {
            VaccineID: detail.VaccineID,
            name: vaccine ? vaccine.VaccineNameTH : "ไม่พบชื่อวัคซีน",
            LotID: detail.LotID,
            RequestQuantity: detail.RequestQuantity,
            RequestDoses: detail.RequestDoses,
            ApprovedQuantity: detail.ApprovedQuantity,
            ConfirmedQuantity: detail.ConfirmedQuantity,
            UnitPrice: detail.UnitPrice,
            DiscountPercent: detail.DiscountPercent,
            DiscountTotal: detail.DiscountTotal,
            TotalPrice: detail.TotalPrice,
            Remark: detail.Remark,
            DoseCount: doseCount,
          };
        });
        const loadedData = {
          ...formData,
          ...reservation,
          ReservationDate: reservation.ReservationDate ? getDateFromISOStringIgnoringTimezone(reservation.ReservationDate) : new Date(),
          deliveryAddress: reservation.deliveryAddress || "",
          deliveryBy: reservation.deliveryBy || "",
          trackingNumber: reservation.trackingNumber || "",
          deliveryStaff: reservation.deliveryStaff || "",
          deliveryDate: reservation.deliveryDate ? new Date(reservation.deliveryDate) : new Date(),
          VaccineDetails: updatedDetails,
        };
        setFormData(loadedData);
        setSavedState(buildComparableState(loadedData));
        // Fetch PeriodEnd for the reservation's DeliveryPeriod
        const periodResponse = await apiService({
          method: "POST",
          url: "/getVaccineReservationPeriod",
          data: { DeliveryPeriod: reservation.DeliveryPeriod },
          sessionId: sessionId || "",
        });
        if (periodResponse.result === "success" && periodResponse.PeriodEnd) {
          setPeriodEnd(new Date(periodResponse.PeriodEnd));
        }
      } else {
        throw new Error(response.error || "ไม่สามารถเรียกดูข้อมูลใบจองนี้ได้");
      }
    } catch (error) {
      console.error("Error fetching reservation:", error);
      // ใบจองที่ไม่มีสิทธิ์เข้าถึง หรือไม่มีอยู่ในระบบ ให้กลับไปหน้ารายการ
      alert(error.message || "ไม่สามารถเรียกดูข้อมูลใบจองนี้ได้");
      navigate("/staffvaccinereservationlist");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchCycles();
      await fetchPickupPoints();
      const vaccines = await fetchVaccineOptions();
      if (reservationId) {
        await fetchReservation(reservationId, vaccines);
      }
    };
    loadData();
  }, [reservationId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  // ใช้กับ SearchableSelect ที่ส่งค่ามาโดยตรงไม่ผ่าน event
  const handleFieldChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const handleDeliveryDateChange = (date) => {
    setFormData({ ...formData, deliveryDate: date });
  };

  const handleVaccineInputChange = (e) => {
    const { name, value } = e.target;
    setNewVaccine({ ...newVaccine, [name]: value });
  };

  const handleQuantityChange = (vaccineId, lotId, value) => {
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId && detail.LotID === lotId) {
        const newQuantity = parseInt(value) || 0;
        const packageSize = vaccineOptions.find((v) => v.VaccineID === vaccineId)?.PackageSize || 1;
        const newTotalPrice = newQuantity * detail.UnitPrice;
        const newDoseCount = Math.ceil(newQuantity * packageSize);
        return { ...detail, RequestQuantity: newQuantity, TotalPrice: newTotalPrice, DoseCount: newDoseCount };
      }
      return detail;
    });
    setFormData({ ...formData, VaccineDetails: updatedDetails });
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "DeliveryPeriod":
      case "PickupPoint":
      case "FarmerName":
      case "FarmerTel":
      case "FarmerEmail":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return (
      formData.DeliveryPeriod &&
      formData.PickupPoint &&
      formData.FarmerName &&
      formData.FarmerTel &&
      formData.FarmerEmail &&
      Object.keys(errors).length === 0
    );
  };

  const handleAddVaccine = () => {
    if (newVaccine.id && newVaccine.unitreq > 0) {
      const selectedVaccine = vaccineOptions.find((v) => v.VaccineID === newVaccine.id);
      if (selectedVaccine) {
        const uop = selectedVaccine.PriceDistribution || "350";
        const packageSize = selectedVaccine.PackageSize || 1;
        const newQuantity = parseInt(newVaccine.unitreq);
        const newDoseCount = Math.ceil(newQuantity * packageSize);
        const totalCost = (newQuantity * parseInt(uop)).toString();
        const vaccineData = {
          VaccineID: newVaccine.id,
          name: selectedVaccine.VaccineNameTH,
          LotID: `LT${Date.now().toString().slice(-4)}`,
          RequestQuantity: newQuantity,
          RequestDoses: 0,
          ApprovedQuantity: 0,
          ConfirmedQuantity: 0,
          UnitPrice: parseFloat(uop),
          DiscountPercent: 0,
          DiscountTotal: 0,
          TotalPrice: parseFloat(totalCost),
          Remark: "",
          DoseCount: newDoseCount,
        };
        setFormData({
          ...formData,
          VaccineDetails: [...formData.VaccineDetails, vaccineData],
        });
        setNewVaccine({ id: "", unitreq: "" });
      } else {
        console.warn(`VaccineID ${newVaccine.id} not found in vaccineOptions`);
      }
    }
  };

  const handleRemoveVaccine = (row) => {
    setFormData({
      ...formData,
      VaccineDetails: formData.VaccineDetails.filter(
        (v) => v.VaccineID !== row.VaccineID || v.LotID !== row.LotID
      ),
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "application/pdf": [],
    },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        setReceiptFile(file);
        setIsEditingFile(false);
      }
    },
  });

  const handleEditFile = () => {
    setIsEditingFile(true);
  };

  const handleRemoveFile = () => {
    setReceiptFile(null);
    setIsEditingFile(false);
  };

  // Handle file preview
  const handleViewFile = (file) => {
    const fileData = {
      name: file.name,
      type: file.type,
      data: URL.createObjectURL(file), // Create a temporary URL for preview
    };
    setSelectedFile(fileData);
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/createVaccineReservations",
        data: { ...formData, VaccineDetails: [] },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setFormData({ ...formData, ReservationID: response.ReservationID });
        setIsSubmitted(true);
        alert("สร้างใบจองสำเร็จ! เลขที่ใบจอง: " + response.ReservationID);
      }
    } catch (error) {
      console.error("Error creating reservation:", error);
      alert("เกิดข้อผิดพลาดในการสร้างใบจอง: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

const handleConfirm = async (overrideData = {}) => {
  // สร้าง mergedFormData ที่รวมค่าจาก formData ปัจจุบันและ overrideData
  const mergedFormData = {
    ...formData,
    ...overrideData,
  };

  // ตรวจสอบความถูกต้องของฟอร์มด้วยข้อมูลที่รวมแล้ว
  if (!isFormValid(mergedFormData)) { 
    alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
    return;
  }

  const submitData = {
    ...mergedFormData, // ใช้ mergedFormData ที่รวมค่าแล้ว
    ReservationDate: mergedFormData.ReservationDate,
    documentDate: mergedFormData.documentDate,
    deliveryDate: mergedFormData.deliveryDate,
  };

  setIsLoading(true);
  try {
    const response = await apiService({
      method: "POST",
      url: "/updateVaccineReservationsByID",
      data: submitData, 
      sessionId: sessionId || "",
    });
    if (response.result === "success") {
      alert("บันทึกใบจองสำเร็จ!");
      setSavedState(buildComparableState(mergedFormData));
      //navigate("/staffvaccinereservationlist");
    }
  } catch (error) {
    console.error("API Error:", error.message);
    alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
  } finally {
    setIsLoading(false);
  }
};

const handleUpdateStatus = async () => {
  if (!selectedNewStatus) {
    alert("กรุณาเลือกสถานะใหม่");
    return;
  }
  
  if (window.confirm(`คุณแน่ใจหรือไม่ว่าต้องการเปลี่ยนสถานะเป็น "${selectedNewStatus}"?`)) {
    setIsUpdatingStatus(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/updateVaccineReservationsByID",
        data: {
          ...formData,
          ReservationStatus: selectedNewStatus,
          UpdatedAt: new Date(),
          UpdatedBy: profile?.Email || "System"
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert("อัปเดตสถานะสำเร็จ!");
        setFormData(prev => ({ ...prev, ReservationStatus: selectedNewStatus }));
        setSelectedNewStatus("");
        // รีโหลดข้อมูลเพื่อให้แน่ใจว่าข้อมูลเป็นปัจจุบัน
        const vaccines = await fetchVaccineOptions();
        await fetchReservation(reservationId, vaccines);
      } else {
        alert("เกิดข้อผิดพลาดในการอัปเดต: " + response.error);
      }
    } catch (error) {
      console.error("Error updating status:", error);
      alert("เกิดข้อผิดพลาดในการอัปเดต: " + error.message);
    } finally {
      setIsUpdatingStatus(false);
    }
  }
};
  
  const handleCancelReservation = async () => {
    if (window.confirm("คุณแน่ใจหรือไม่ว่าต้องการยกเลิกการจองนี้?")) {
      setIsLoading(true);
      try {
        const response = await apiService({
          method: "POST",
          url: "/cancelVaccineReservationsByID",
          data: { ReservationID: formData.ReservationID },
          sessionId: sessionId || "",
        });
        if (response.result === "success") {
          alert("ยกเลิกการจองสำเร็จ!");
          navigate("/staffvaccinereservationlist");
        } else {
          alert("เกิดข้อผิดพลาดในการยกเลิก: " + response.error);
        }
      } catch (error) {
        console.error("Error cancelling reservation:", error);
        alert("เกิดข้อผิดพลาดในการยกเลิก: " + error.message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const getWarehouseName = async (warehouseId) => {
    const warehouse = pickupPoints.find((point) => point.WarehouseID === warehouseId);
    if (warehouse) {
      return warehouse.WarehouseName;
    }
    return await fetchWarehouseById(warehouseId);
  };

  /**
   * เตรียมเอกสาร PDF จากข้อมูลล่าสุดในฐานข้อมูล
   * เพื่อไม่ให้เอกสารที่พิมพ์ออกไปมีค่าที่ยังไม่ได้บันทึกจากในฟอร์ม
   */
  const createReservationDoc = async () => {
    const printData = await fetchReservationForPrint({
      reservationID: formData.ReservationID,
      sessionId,
      vaccineOptions,
    });
    const warehouseName = await getWarehouseName(
      printData.reservation.PickupPoint
    );
    const doc = await buildReservationDoc({
      ...printData,
      warehouseName,
      periodEndDate: periodEnd ? formatThaiDate(periodEnd) : " ",
    });
    return { doc, printData };
  };

  const printTable = async () => {
    setIsLoading(true);
    try {
      const { doc } = await createReservationDoc();
      if (!printReservationDoc(doc)) {
        alert(
          "ไม่สามารถเปิดหน้าต่างพิมพ์เอกสารได้ กรุณาอนุญาตการเปิดหน้าต่างใหม่ (pop-up) ของเว็บไซต์นี้"
        );
      }
    } catch (error) {
      alert("ไม่สามารถพิมพ์ใบจองได้: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const exportToPDF = async () => {
    setIsLoading(true);
    try {
      const { doc, printData } = await createReservationDoc();
      doc.save(`${printData.reservation.ReservationID}.pdf`);
    } catch (error) {
      alert("ไม่สามารถส่งออกเอกสารได้: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  //const isEditable = formData.ReservationStatus === "ใบจองใหม่";
  //const isSaveButtonVisible = periodEnd ? new Date() <= periodEnd : true;
  const today = new Date();
  today.setHours(0, 0, 0, 0); // ตัดเวลาออก เหลือแค่วันที่

  const end = new Date(periodEnd);
  end.setHours(23, 59, 59, 999); // ให้สิ้นสุดจนถึงสิ้นวัน

  const isEditable =
  formData.ReservationStatus === "ใบจองใหม่" &&
  (periodEnd ? today <= end : true);

  // ตรวจสอบว่า PeriodEnd < วันที่ปัจจุบัน เพื่อแสดงปุ่มปรับสถานะ
  const isPeriodExpired = periodEnd ? today > end : false;
  
  // ฟังก์ชันสำหรับตรวจสอบสถานะที่สามารถเปลี่ยนได้
  const getAvailableStatusOptions = (currentStatus) => {
    if (!isPeriodExpired) return [];
    
    switch (currentStatus) {
      case "ใบจองใหม่":
        return [
          "รอการอนุมัติ", 
          "ยืนยันการจอง",
          "พร้อมรับวัคซีน",
          "รับวัคซีนแล้ว"
        ];
      case "รอการอนุมัติ":
        return [
          "ยืนยันการจอง", 
          "พร้อมรับวัคซีน",
          "รับวัคซีนแล้ว"
        ];
      case "ยืนยันการจอง":
        return [
          "พร้อมรับวัคซีน",
          "รับวัคซีนแล้ว"
        ];
      case "พร้อมรับวัคซีน":
        return [
          "รับวัคซีนแล้ว"
        ];
      default:
        return [];
    }
  };

  const availableStatusOptions = getAvailableStatusOptions(formData.ReservationStatus);


  const totalQuantity = formData.VaccineDetails.reduce((sum, detail) => sum + detail.RequestQuantity, 0);
  const totalPrice = formData.VaccineDetails.reduce((sum, detail) => sum + detail.TotalPrice, 0);

  // มีการแก้ไขที่ยังไม่บันทึก จึงยังพิมพ์เอกสารไม่ได้
  const hasUnsavedChanges =
    savedState !== null && savedState !== buildComparableState(formData);

  const goBackToList = () => navigate("/staffvaccinereservationlist");

  // ถ้ายังมีการแก้ไขที่ไม่ได้บันทึก ให้ยืนยันก่อนออกจากหน้า เพื่อไม่ให้ข้อมูลที่แก้ไว้หายไปโดยไม่รู้ตัว
  const handleBack = () => {
    if (hasUnsavedChanges) {
      setIsLeaveAlertOpen(true);
      return;
    }
    goBackToList();
  };

  // มุมมองการ์ดส่งมาแค่ VaccineID จึงต้องหา LotID ของรายการนั้นให้เอง
  const handleCardQuantityChange = (vaccineId, value) => {
    const detail = formData.VaccineDetails.find(
      (item) => item.VaccineID === vaccineId
    );
    handleQuantityChange(vaccineId, detail?.LotID, value);
  };

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faSyringeIcon} />
          <span>รหัส / ชื่อวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.VaccineID,
      sortable: true,
      cell: (row) => (
        <Box py={1}>
          <Text fontWeight="medium">{row.VaccineID}</Text>
          <Text fontSize="sm" color="gray.600">
            {row.name}
          </Text>
        </Box>
      ),
      minWidth: "240px",
      wrap: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCalculator} />
          <span>จำนวน (ขวด)</span>
        </HStack>
      ),
      selector: (row) => row.RequestQuantity,
      sortable: true,
      right: !isEditable,
      cell: (row) =>
        isEditable ? (
          <Input
            type="number"
            value={row.RequestQuantity}
            onChange={(e) =>
              handleQuantityChange(row.VaccineID, row.LotID, e.target.value)
            }
            min={0}
            size="sm"
            maxW="110px"
          />
        ) : (
          formatNumber(row.RequestQuantity)
        ),
      minWidth: "130px",
      grow: 0,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCapsules} />
          <span>จำนวน (โดส)</span>
        </HStack>
      ),
      selector: (row) => row.DoseCount,
      format: (row) => formatNumber(row.DoseCount),
      sortable: true,
      right: true,
      minWidth: "120px",
      grow: 0,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faMoneyBillWave} />
          <span>ราคา/ขวด (บาท)</span>
        </HStack>
      ),
      selector: (row) => row.UnitPrice,
      format: (row) => formatNumber(row.UnitPrice, { minimumFractionDigits: 2 }),
      sortable: true,
      right: true,
      minWidth: "150px",
      grow: 0,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faDollarSign} />
          <span>ราคารวม (บาท)</span>
        </HStack>
      ),
      selector: (row) => row.TotalPrice,
      format: (row) =>
        formatNumber(row.TotalPrice, { minimumFractionDigits: 2 }),
      sortable: true,
      right: true,
      minWidth: "150px",
      grow: 0,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faGears} />
          <span>จัดการ</span>
        </HStack>
      ),
      cell: (row) => (
        <IconButton.Delete
          icon={faTrash}
          onClick={() => handleRemoveVaccine(row)}
          label="ลบ"
          size="sm"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      minWidth: "130px",
      omit: !isEditable,
    },
  ];

  return (
    <Box
      p={{ base: 3, sm: 4, md: 6 }}
      m={{ base: 2, md: 4 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <StandardPageTitle title="รายละเอียดใบจองวัคซีน (สำหรับเจ้าหน้าที่)" />

      <Box
        borderWidth={1}
        borderRadius="lg"
        boxShadow="lg"
        p={{ base: 3, md: 5 }}
        mb={6}
      >
        <StandardHeader
          title="ข้อมูลเอกสารการสั่งจองวัคซีน"
          bgColor="iBlue.600"
          icon={faFileLines}
        />
        <Grid
          templateColumns={{
            base: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(4, 1fr)",
          }}
          gap={{ base: 4, md: 6 }}
          p={{ base: 2, md: 5 }}
        >
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                เลขที่ใบสั่งจองวัคซีน
              </FormLabel>
              <Input
                name="ReservationID"
                value={formData.ReservationID}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, lg: 1 }}>
            <FormControl isRequired isInvalid={!!errors.DeliveryPeriod}>
              <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                รอบการจัดส่งวัคซีน
              </FormLabel>
              <Input
                type="text"
                name="DeliveryPeriod"
                value={formData.DeliveryPeriod}
                onChange={handleInputChange}
                placeholder="รอบการจัดส่ง"
                isReadOnly // ✅ ถ้าไม่ต้องการให้ผู้ใช้พิมพ์เอง
              />
              <FormErrorMessage>{errors.DeliveryPeriod}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                วันที่สั่งจอง
              </FormLabel>
              <Input
                type="text"
                value={formatThaiDate(formData.ReservationDate)}
                isReadOnly
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl isRequired isInvalid={!!errors.PickupPoint}>
              <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                จุดจำหน่าย
              </FormLabel>
              <SearchableSelect
                name="PickupPoint"
                value={formData.PickupPoint}
                onChange={(value) => handleFieldChange("PickupPoint", value)}
                options={pickupPointOptions}
                placeholder="ค้นหา / เลือกจุดจำหน่าย"
                isInvalid={!!errors.PickupPoint}
              />
              <FormHelperText fontSize="xs">
                จุดที่ต้องการรับวัคซีน
              </FormHelperText>
              <FormErrorMessage>{errors.PickupPoint}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired isInvalid={!!errors.FarmerName}>
              <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                ชื่อเกษตรกร
              </FormLabel>
              <Input
                name="FarmerName"
                value={formData.FarmerName}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
              <FormErrorMessage>{errors.FarmerName}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                เลขประจำตัวผู้เสียภาษี
              </FormLabel>
              <Input
                name="RefTaxID"
                value={formData.RefTaxID}
                onChange={handleInputChange}
                colorScheme="iBlue"
                maxLength={30}
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl isRequired isInvalid={!!errors.FarmerTel}>
              <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                เบอร์โทรติดต่อ
              </FormLabel>
              <Input
                name="FarmerTel"
                value={formData.FarmerTel}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
              <FormErrorMessage>{errors.FarmerTel}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl isRequired isInvalid={!!errors.FarmerEmail}>
              <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                E-mail Address
              </FormLabel>
              <Input
                name="FarmerEmail"
                value={formData.FarmerEmail}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
              <FormErrorMessage>{errors.FarmerEmail}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, sm: 2, lg: 4 }}>
            <FormControl mb={4}>
              <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                วัตถุประสงค์การใช้งาน / รายละเอียดเพิ่มเติม
              </FormLabel>
              <Textarea
                name="UsageObjective"
                value={formData.UsageObjective}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, sm: 1, lg: 2 }}>
            <FormControl>
              <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                สถานะรายการ
              </FormLabel>
              <Box pt={1}>
                <ReservationStatusDot
                  status={formData.ReservationStatus}
                  fontSize={{ base: "md", md: "lg" }}
                />
              </Box>
            </FormControl>
          </GridItem>
          <GridItem colSpan={{ base: 1, sm: 1, lg: 2 }}>
            <FormControl>
              <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                วันที่แก้ไขข้อมูล
              </FormLabel>
              <Text pt={1} fontSize={{ base: "md", md: "lg" }}>
                {formatThaiDateTime(formData.UpdatedAt)}
              </Text>
            </FormControl>
          </GridItem>
        </Grid>
        {!isSubmitted && (
          <Box p={{ base: 2, md: 5 }}>
            <Flex
              direction={{ base: "column", md: "row" }}
              justifyContent={{ md: "flex-end" }}
              alignItems={{ base: "stretch", md: "center" }}
              gap={4}
              w="100%"
            >
              <IconButton.Cancel
                onClick={() => navigate("/staffvaccinereservationlist")}
              />
              <IconButton.Submit
                label="สร้างใบจองวัคซีน"
                onClick={handleSubmit}
              />
            </Flex>
          </Box>
        )}
      </Box>

      {isSubmitted && (
        <>
          {isEditable && (
            <Box
              borderWidth={1}
              borderRadius="lg"
              boxShadow="lg"
              p={{ base: 3, md: 5 }}
              mb={6}
            >
              <StandardHeader
                title="เลือกวัคซีนที่ต้องการสั่งจอง"
                bgColor="iPurple.600"
                icon={faSyringe}
              />
              <Grid
                templateColumns={{ base: "1fr", md: "2fr 1fr auto" }}
                gap={{ base: 4, md: 6 }}
                p={{ base: 2, md: 5 }}
                alignItems="end"
              >
                <FormControl>
                  <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                    เลือกวัคซีน
                  </FormLabel>
                  <SearchableSelect
                    name="id"
                    value={newVaccine.id}
                    onChange={(value) =>
                      setNewVaccine((prev) => ({ ...prev, id: value }))
                    }
                    options={vaccineSelectOptions}
                    placeholder="พิมพ์รหัสหรือชื่อวัคซีนเพื่อค้นหา..."
                    noOptionsMessage="ไม่พบวัคซีนที่ค้นหา"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                    จำนวน (ขวด)
                  </FormLabel>
                  <Input
                    name="unitreq"
                    type="number"
                    value={newVaccine.unitreq}
                    onChange={handleVaccineInputChange}
                    min={1}
                    colorScheme="iPurple"
                  />
                </FormControl>
                <IconButton.Add
                  onClick={handleAddVaccine}
                  w={{ base: "full", md: "auto" }}
                />
              </Grid>
            </Box>
          )}

          <Box
            borderWidth={1}
            borderRadius="lg"
            boxShadow="lg"
            p={{ base: 3, md: 5 }}
            mb={6}
          >
            <StandardHeader
              title="รายการวัคซีนที่ต้องการสั่งจอง"
              bgColor="iOrange.600"
              icon={faTable}
            />
            <Box p={{ base: 0, md: 5 }}>
              {useCardView ? (
                <VStack spacing={4} align="stretch">
                  <VaccineDetailCards
                    details={formData.VaccineDetails}
                    isEditable={isEditable}
                    onQuantityChange={handleCardQuantityChange}
                    onRemove={handleRemoveVaccine}
                  />
                  <TotalsSummary
                    totalQuantity={totalQuantity}
                    totalPrice={totalPrice}
                  />
                </VStack>
              ) : (
                <Box overflowX="auto">
                  <DataTable
                    columns={columns}
                    data={formData.VaccineDetails}
                    pagination
                    highlightOnHover
                    striped
                    responsive
                    customStyles={tableCustomStyles}
                    subHeader
                    subHeaderComponent={
                      <TotalsSummary
                        totalQuantity={totalQuantity}
                        totalPrice={totalPrice}
                      />
                    }
                  />
                </Box>
              )}
            </Box>
          </Box>

          {formData.PickupPoint === "WH001" && (
            <Box
              borderWidth={1}
              borderRadius="lg"
              boxShadow="lg"
              p={{ base: 3, md: 5 }}
              mb={6}
            >
              <StandardHeader
                title="การชำระเงิน และการจัดส่งวัคซีน"
                bgColor="iGreen.600"
                icon={faTruck}
              />
              <Box p={{ base: 0, md: 5 }}>
                <Grid
                  templateColumns={{
                    base: "1fr",
                    sm: "repeat(2, 1fr)",
                    lg: "repeat(3, 1fr)",
                  }}
                  gap={{ base: 4, md: 6 }}
                  p={{ base: 2, md: 5 }}
                >
                  <GridItem colSpan={{ base: 1, sm: 2, lg: 3 }}>
                    <FormControl>
                      <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                        ที่อยู่ในการจัดส่งวัคซีน
                      </FormLabel>
                      <Input
                        name="deliveryAddress"
                        value={formData.deliveryAddress}
                        onChange={handleInputChange}
                        colorScheme="iGreen"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={1}>
                    <FormControl>
                      <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                        จัดส่งโดย
                      </FormLabel>
                      <Input
                        name="deliveryBy"
                        value={formData.deliveryBy}
                        onChange={handleInputChange}
                        colorScheme="iGreen"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={1}>
                    <FormControl>
                      <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                        เลขที่พัสดุภัณฑ์
                      </FormLabel>
                      <Input
                        name="trackingNumber"
                        value={formData.trackingNumber}
                        onChange={handleInputChange}
                        colorScheme="iGreen"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={1}>
                    <FormControl>
                      <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                        เจ้าหน้าที่ผู้จัดส่ง
                      </FormLabel>
                      <Input
                        name="deliveryStaff"
                        value={formData.deliveryStaff}
                        onChange={handleInputChange}
                        colorScheme="iGreen"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={1}>
                    <FormControl>
                      <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                        วันที่จัดส่งวัคซีน
                      </FormLabel>
                      <CustomDatePicker
                        value={formData.deliveryDate}
                        onChange={handleDeliveryDateChange}
                        colorScheme="iGreen"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={{ base: 1, sm: 2 }}>
                    <FormControl>
                      <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                        แนบไฟล์ใบเสร็จรับเงิน (รูปภาพหรือ PDF เท่านั้น)
                      </FormLabel>
                      {!receiptFile || isEditingFile ? (
                        <Box
                          {...getRootProps()}
                          border="2px dashed"
                          borderColor={isDragActive ? "green.500" : "gray.300"}
                          borderRadius="md"
                          p={4}
                          textAlign="center"
                          cursor="pointer"
                          bg={isDragActive ? "green.50" : "transparent"}
                        >
                          <input {...getInputProps()} />
                          <FontAwesomeIcon icon={faFileUpload} size="2x" color="gray" />
                          <Box mt={2}>
                            {isDragActive
                              ? "วางไฟล์ที่นี่ ..."
                              : "ลากและวางไฟล์ใบเสร็จที่นี่ หรือคลิกเพื่อเลือกไฟล์ (JPEG, PNG, PDF)"}
                          </Box>
                        </Box>
                      ) : (
                        <Flex mt={2} gap={2} wrap="wrap" align="center">
                          <Text
                            color="blue.500"
                            cursor="pointer"
                            onClick={() => handleViewFile(receiptFile)}
                          >
                            {receiptFile.name}
                          </Text>
                          <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={handleEditFile}
                            leftIcon={<FontAwesomeIcon icon={faEdit} />}
                          >
                            แก้ไข
                          </Button>
                          <Button
                            size="sm"
                            colorScheme="red"
                            onClick={handleRemoveFile}
                            leftIcon={<FontAwesomeIcon icon={faTrash} />}
                          >
                            ลบไฟล์
                          </Button>
                        </Flex>
                      )}
                    </FormControl>
                  </GridItem>
                </Grid>
              </Box>
            </Box>
          )}

          {/* ส่วนสำหรับปรับสถานะเมื่อ PeriodEnd < วันที่ปัจจุบัน (เฉพาะเจ้าหน้าที่) */}
          {isPeriodExpired && availableStatusOptions.length > 0 && (
            <Box
              borderWidth={1}
              borderRadius="lg"
              boxShadow="lg"
              p={{ base: 3, md: 5 }}
              mb={6}
              bgColor="orange.50"
            >
              <StandardHeader
                title="ปรับปรุงสถานะการจอง (รอบการจัดส่งนี้ พ้นกำหนดการสั่งจองแล้ว)"
                bgColor="orange.500"
                icon={faGears}
              />

              <Grid
                templateColumns={{ base: "1fr", md: "2fr 1fr" }}
                gap={{ base: 4, md: 6 }}
                p={{ base: 2, md: 5 }}
                alignItems="end"
              >
                <FormControl>
                  <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                    สถานะปัจจุบัน: <strong>{formData.ReservationStatus}</strong>
                  </FormLabel>
                  <Select
                    placeholder="เลือกสถานะใหม่"
                    value={selectedNewStatus}
                    onChange={(e) => setSelectedNewStatus(e.target.value)}
                    colorScheme="orange"
                    bgColor="white"
                  >
                    {availableStatusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  colorScheme="saveScheme"
                  onClick={handleUpdateStatus}
                  isLoading={isUpdatingStatus}
                  loadingText="กำลังอัปเดต..."
                  isDisabled={!selectedNewStatus}
                  w="100%"
                >
                  ปรับปรุงสถานะการจอง
                </Button>
              </Grid>
            </Box>
          )}

          <Box p={{ base: 2, md: 5 }}>
            {hasUnsavedChanges && (
              <Alert status="warning" borderRadius="md" mb={4}>
                <AlertIcon />
                <Box>
                  <AlertTitle fontSize={{ base: "sm", md: "md" }}>
                    มีการแก้ไขที่ยังไม่ได้บันทึก
                  </AlertTitle>
                  <AlertDescription fontSize={{ base: "xs", md: "sm" }}>
                    กรุณากดปุ่มบันทึกก่อน จึงจะพิมพ์ใบจองหรือส่งออกเป็น PDF ได้
                    เพื่อให้เอกสารที่พิมพ์ตรงกับข้อมูลในระบบ
                  </AlertDescription>
                </Box>
              </Alert>
            )}
            <Flex
              direction={{ base: "column", md: "row" }}
              justifyContent={{ md: "flex-end" }}
              alignItems={{ base: "stretch", md: "center" }}
              gap={4}
              wrap="wrap"
              w="100%"
            >
              <IconButton.Back onClick={handleBack} label="ย้อนกลับ" />
              <IconButton.Cancel
                label="ยกเลิกการจอง"
                onClick={handleCancelReservation}
              />
              {isEditable && (
                <IconButton.Save onClick={() => handleConfirm({})} isLoading={isLoading} />
              )}
              <Tooltip
                label="กรุณาบันทึกการแก้ไขก่อนพิมพ์เอกสาร"
                isDisabled={!hasUnsavedChanges}
                placement="top"
                hasArrow
              >
                <Box w={{ base: "100%", md: "auto" }}>
                  <IconButton.Print
                    onClick={printTable}
                    label="พิมพ์ใบจอง"
                    isDisabled={hasUnsavedChanges}
                    w="100%"
                  />
                </Box>
              </Tooltip>
              <Tooltip
                label="กรุณาบันทึกการแก้ไขก่อนส่งออกเอกสาร"
                isDisabled={!hasUnsavedChanges}
                placement="top"
                hasArrow
              >
                <Box w={{ base: "100%", md: "auto" }}>
                  <IconButton.ExportPdf
                    onClick={exportToPDF}
                    isDisabled={hasUnsavedChanges}
                    w="100%"
                  />
                </Box>
              </Tooltip>
            </Flex>
          </Box>
        </>
      )}

      <AlertDialog
        isOpen={isLeaveAlertOpen}
        leastDestructiveRef={leaveCancelRef}
        onClose={() => setIsLeaveAlertOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              มีการแก้ไขที่ยังไม่ได้บันทึก
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text>
                ข้อมูลใบจองที่แก้ไขยังไม่ได้บันทึกลงระบบ หากย้อนกลับตอนนี้
                การแก้ไขทั้งหมดจะหายไป
              </Text>
              <Text mt={3}>คุณต้องการดำเนินการอย่างไร :</Text>
            </AlertDialogBody>
            <AlertDialogFooter
              as={Flex}
              direction={{ base: "column", sm: "row" }}
              gap={3}
              justifyContent="flex-end"
            >
              <Button
                ref={leaveCancelRef}
                colorScheme="backScheme"
                leftIcon={<FontAwesomeIcon icon={faXmark} />}
                onClick={() => setIsLeaveAlertOpen(false)}
                w={{ base: "100%", sm: "auto" }}
              >
                กลับไปแก้ไขต่อ
              </Button>
              <Button
                colorScheme="cancelScheme"
                leftIcon={<FontAwesomeIcon icon={faTrash} />}
                onClick={() => {
                  setIsLeaveAlertOpen(false);
                  goBackToList();
                }}
                w={{ base: "100%", sm: "auto" }}
              >
                ออกโดยไม่บันทึก
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Modal for viewing files */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>ดูไฟล์: {selectedFile?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedFile?.type.startsWith("image/") ? (
              <Image src={selectedFile.data} alt={selectedFile.name} maxW="100%" />
            ) : selectedFile?.type === "application/pdf" ? (
              <Box>
                <Text>ไฟล์ PDF: {selectedFile.name}</Text>
                <Button
                  as="a"
                  href={selectedFile.data}
                  download={selectedFile.name}
                  colorScheme="blue"
                  mt={2}
                >
                  ดาวน์โหลด PDF
                </Button>
              </Box>
            ) : (
              <Text>ไม่สามารถแสดงไฟล์นี้ได้</Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default StaffVaccineReservationDetail;